import { extend, override } from 'flarum/common/extend';
import Page from 'flarum/common/components/Page';
import UserPage from 'flarum/forum/components/UserPage';

/**
 * Scroll position on navigation. Core already starts every new page at the top, with two
 * exceptions that this module narrows:
 *
 * 1. A discussion list (IndexPage) opened right after a discussion restores the old list's
 *    scroll position — meant for Back, but core does it for any navigation, so a section
 *    opened by a click could start at the bottom. It now happens only on history navigation
 *    (Back / Forward, i.e. the router's popstate); a click opens the list at the top.
 * 2. A discussion opened from a list jumps to the reader's first unread post; when nothing
 *    is unread that target is the last post, so read discussions opened at the bottom. They
 *    now open at the start; unread replies, search results and links to a specific post
 *    still go to their post (core's own scrolling).
 *
 * 3. The sections of a profile (Сообщения, Темы, Мои загрузки, Настройки, Безопасность) are separate
 *    routes, so each opened at the top like a new page. A click in the profile's own section list
 *    now keeps the reader where they were: the new section's page skips core's scroll-to-top, the
 *    old position is restored before the first paint, and the page keeps its height until the new
 *    list has loaded (a shorter section then lets the browser bring the position within reach).
 *    Back / Forward and every other navigation behave as before.
 *
 * Modals, the composer, dropdowns and the lightbox do not change the route and are not affected.
 */
let historyNavigation = false;

/** A switch between sections of the profile being viewed: where the reader was, and the page height. */
let profileSwitch = null;
export default function addScrollBehaviour() {
  // Mithril's router reacts to popstate; this listener is registered before the router starts,
  // so the flag is set before the page for that history entry is created.
  window.addEventListener('popstate', () => {
    historyNavigation = true;
  });

  // a click on a section of the profile being viewed (capture: before the link changes the route)
  document.addEventListener('click', e => {
    const link = e.target?.closest?.('.UserPage .sideNav a[href]');
    if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    profileSwitch = {
      y: window.scrollY,
      height: document.documentElement.scrollHeight
    };
  }, true);
  extend(Page.prototype, 'oninit', function () {
    if (profileSwitch && !historyNavigation && this instanceof UserPage) {
      this.scrollTopOnCreate = false;
      this.tpKeepScroll = profileSwitch;
    }
    profileSwitch = null;
  });

  // consumed once the page of that navigation exists
  extend(Page.prototype, 'oncreate', function () {
    historyNavigation = false;
    const keep = this.tpKeepScroll;
    if (!keep) return;
    const body = document.body.style;
    body.minHeight = `${keep.height}px`;
    window.scrollTo(0, keep.y);

    // release the height once the section's content is there (or after a while)
    const start = Date.now();
    const settle = () => {
      const loading = document.querySelector('.UserPage .Page-content .LoadingIndicator');
      if (!loading && Date.now() - start > 120 || Date.now() - start > 2500) body.minHeight = '';else requestAnimationFrame(settle);
    };
    requestAnimationFrame(settle);
  });
  extend('flarum/forum/components/IndexPage', 'oninit', function () {
    if (!historyNavigation) this.lastDiscussion = undefined;
  });
  override('flarum/forum/components/DiscussionListItem', 'getJumpTo', function (original) {
    const jumpTo = original();
    const discussion = this.attrs.discussion;
    if (this.attrs.jumpTo || this.attrs.params?.q) return jumpTo;
    const lastRead = discussion.lastReadPostNumber() || 0;
    const last = discussion.lastPostNumber() ?? 0;
    return lastRead >= last ? 0 : jumpTo;
  });
}